import React from 'react';
import { Plane, ShoppingCart, Settings, Wrench, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../data/mock';
import { Reveal } from '../components/Reveal';

const Services = () => {
  const iconMap = {
    Plane: Plane,
    ShoppingCart: ShoppingCart,
    Settings: Settings,
    Wrench: Wrench
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1540962351504-03099e0a754b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxwcml2YXRlJTIwamV0fGVufDB8fHx8MTc3MTA0NTY2Mnww&ixlib=rb-4.1.0&q=85"
            alt="Services"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal y={30}>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Our Services</h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">
              Comprehensive aviation solutions tailored to your needs
            </p>
          </Reveal>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <Reveal
                  key={service.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    !isEven ? 'lg:grid-flow-dense' : ''
                  }`}
                >
                  <div className={!isEven ? 'lg:col-start-2' : ''}>
                    <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="text-slate-300" size={32} />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <CheckCircle className="text-slate-400 flex-shrink-0" size={20} />
                          <span className="text-slate-300">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link
                      to="/contact"
                      className="inline-flex items-center space-x-2 px-6 py-3 btn-chrome rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                    >
                      <span>Request Service</span>
                      <ArrowRight size={18} />
                    </Link>
                  </div>

                  <div className={!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}>
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent"></div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Additional Capabilities</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Supporting services that complement our core offerings
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Aircraft Transition',
                description: 'Seamless aircraft delivery and redelivery services with complete documentation.',
                icon: '✈️'
              },
              {
                title: 'Technical Records',
                description: 'Comprehensive maintenance records management and documentation services.',
                icon: '📋'
              },
              {
                title: 'Parts Supply',
                description: 'Global sourcing of genuine aircraft parts and components.',
                icon: '⚙️'
              },
              {
                title: 'AOG Support',
                description: '24/7 Aircraft on Ground support to minimize downtime.',
                icon: '🚨'
              },
              {
                title: 'Training Services',
                description: 'Technical training for maintenance and operations personnel.',
                icon: '🎓'
              },
              {
                title: 'Consulting',
                description: 'Expert aviation consulting for fleet planning and optimization.',
                icon: '💼'
              }
            ].map((capability, index) => (
              <Reveal
                key={index}
                delay={index * 0.06}
                className="bg-slate-900 rounded-xl p-6 border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-700/20 hover:-translate-y-2"
              >
                <div className="text-4xl mb-4">{capability.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{capability.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{capability.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Reveal className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Need a Custom Solution?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Our team is ready to discuss your specific aviation requirements
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 px-8 py-4 btn-chrome rounded-lg font-semibold text-lg transition-all duration-300 shadow-2xl hover:shadow-white/20 hover:scale-105"
          >
            <span>Get in Touch</span>
            <ArrowRight size={20} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Services;
